// Read-only mirror of the technician's annotations on the customer screen.
//
// Stroke points are normalized [0, 1] over the video frame — the customer
// computes their own letterbox locally, so it doesn't matter if their
// baseW/baseH disagrees with the tech's. See tech/src/components/AnnotationCanvas.js
// for the wire format details.
import React, { useEffect, useRef } from 'react';

const REF_HEIGHT = 720;

function isDrawn(s) {
  if (!s.points || s.points.length === 0) return false;
  if (s.tool === 'pen') return s.points.length > 1;
  return s.points.length >= 2;
}

function letterboxFit(canvasW, canvasH, baseW, baseH) {
  if (!baseW || !baseH || canvasW <= 0 || canvasH <= 0) {
    return { drawW: canvasW, drawH: canvasH, offX: 0, offY: 0 };
  }
  const scale = Math.min(canvasW / baseW, canvasH / baseH);
  const drawW = baseW * scale;
  const drawH = baseH * scale;
  return {
    drawW,
    drawH,
    offX: (canvasW - drawW) / 2,
    offY: (canvasH - drawH) / 2,
  };
}

function drawStroke(ctx, stroke, drawW, drawH) {
  if (drawW <= 0 || drawH <= 0) return;
  const pxPerUnit = Math.min(drawW, drawH) / REF_HEIGHT;
  const lineWidth = stroke.width * pxPerUnit;
  ctx.save();
  ctx.strokeStyle = stroke.color;
  ctx.fillStyle = stroke.color;
  ctx.lineWidth = lineWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const pts = stroke.points.map(([nx, ny]) => [nx * drawW, ny * drawH]);
  if (stroke.tool === 'pen' && pts.length > 1) {
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.stroke();
  } else if (stroke.tool === 'arrow' && pts.length >= 2) {
    const [x1, y1] = pts[0];
    const [x2, y2] = pts[pts.length - 1];
    ctx.beginPath();
    ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    const angle = Math.atan2(y2 - y1, x2 - x1);
    const head = (14 + stroke.width * 0.8) * pxPerUnit;
    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - head * Math.cos(angle - Math.PI / 7), y2 - head * Math.sin(angle - Math.PI / 7));
    ctx.lineTo(x2 - head * Math.cos(angle + Math.PI / 7), y2 - head * Math.sin(angle + Math.PI / 7));
    ctx.closePath(); ctx.fill();
  } else if (stroke.tool === 'rect' && pts.length >= 2) {
    const [x1, y1] = pts[0]; const [x2, y2] = pts[pts.length - 1];
    ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
  } else if (stroke.tool === 'circle' && pts.length >= 2) {
    const [x1, y1] = pts[0]; const [x2, y2] = pts[pts.length - 1];
    const r = Math.hypot(x2 - x1, y2 - y1);
    ctx.beginPath(); ctx.arc(x1, y1, r, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.restore();
}

export default function AnnotationOverlay({ strokes, baseW, baseH }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.scale(dpr, dpr);
    if (!strokes || !strokes.length) return;
    const { drawW, drawH, offX, offY } = letterboxFit(w, h, baseW, baseH);
    ctx.translate(offX, offY);
    for (const s of strokes) {
      if (!isDrawn(s)) continue;
      drawStroke(ctx, s, drawW, drawH);
    }
  }, [strokes, baseW, baseH]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
    />
  );
}
